import PlusFilledIcon from "../../../../../assets/svg/plus-filled-icon";
import ChevronIcon from "../../../../../assets/svg/chevron-icon";
import type { FilterFieldOption } from "../../HelpDesk/components/CreateView/utils/constants";
import { DealsFilterConditionRow } from "./DealsFilterConditionRow";
import {
  type FilterConditionUI,
  type FilterGroupUI,
  createEmptyCondition,
} from "../utils/dealsViewConstants";

interface DealsFilterGroupBlockProps {
  group: FilterGroupUI;
  onGroupChange: (updated: FilterGroupUI) => void;
  onDeleteGroup: () => void;
  isFirstGroup?: boolean;
  fieldOptions?: FilterFieldOption[];
  filterGroups?: FilterGroupUI[];
}

const LogicalOperatorToggle = ({
  value,
  onChange,
  disabled = false,
}: {
  value: "and" | "or";
  onChange: (val: "and" | "or") => void;
  disabled?: boolean;
}) => {
  if (disabled) {
    return (
      <span className="px-2 py-0.5 text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk uppercase select-none">
        {value?.toUpperCase()}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onChange(value === "and" ? "or" : "and")}
      className="flex items-center gap-0.5 px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded-md text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer transition-colors uppercase"
    >
      {value?.toUpperCase()}
      <ChevronIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
    </button>
  );
};

export const DealsFilterGroupBlock = ({
  group,
  onGroupChange,
  onDeleteGroup,
  isFirstGroup = false,
  fieldOptions,
  filterGroups = [],
}: DealsFilterGroupBlockProps) => {
  const isNested = group?.conditions?.length > 1;

  const handleConditionChange = (idx: number, updated: FilterConditionUI) => {
    const newConditions = [...(group?.conditions ?? [])];
    newConditions[idx] = updated;
    onGroupChange({ ...group, conditions: newConditions });
  };

  const handleDeleteCondition = (idx: number) => {
    const newConditions = group?.conditions?.filter((_, i) => i !== idx);
    if (newConditions?.length === 0) {
      onDeleteGroup();
      return;
    }
    onGroupChange({ ...group, conditions: newConditions });
  };

  const handleAddNestedFilter = () => {
    onGroupChange({
      ...group,
      conditions: [...(group?.conditions ?? []), createEmptyCondition()],
    });
  };

  const handleLogicalOperatorChange = (val: "and" | "or") => {
    onGroupChange({ ...group, logical_operator: val });
  };

  if (!isNested) {
    return (
      <div className="flex flex-col">
        <div className={`flex items-center ${isFirstGroup ? "pl-[70px]" : ""}`}>
          <DealsFilterConditionRow
            condition={group?.conditions?.[0]}
            onChange={(updated) => handleConditionChange(0, updated)}
            onDelete={() => handleDeleteCondition(0)}
            fieldOptions={fieldOptions}
            filterGroups={filterGroups}
          />
        </div>
        <div className={`pt-2 ${isFirstGroup ? "pl-[70px]" : ""}`}>
          <button
            type="button"
            onClick={handleAddNestedFilter}
            className="flex items-center gap-1 cursor-pointer group"
          >
            <PlusFilledIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk group-hover:underline">
              Add Nested Filter
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className={isFirstGroup ? "pl-[70px]" : ""}>
        <div className="bg-hover-effect dark:bg-hover-effect-dk rounded-lg px-2 pb-2 flex flex-col gap-0.5">
          {group?.conditions?.map((condition, idx) => (
            <div key={idx} className="flex flex-col">
              {idx > 0 && (
                <div className="py-1">
                  <LogicalOperatorToggle
                    value={group?.logical_operator}
                    onChange={handleLogicalOperatorChange}
                    disabled={idx > 1}
                  />
                </div>
              )}
              <DealsFilterConditionRow
                condition={condition}
                onChange={(updated) => handleConditionChange(idx, updated)}
                onDelete={() => handleDeleteCondition(idx)}
                showBackground={false}
                fieldOptions={fieldOptions}
                filterGroups={filterGroups}
              />
            </div>
          ))}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleAddNestedFilter}
              className="flex items-center gap-1 cursor-pointer group"
            >
              <PlusFilledIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
              <span className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk group-hover:underline">
                Add Nested Filter
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
